import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import type { Folder } from "@/types";
import {
  BookOpen,
  BookText,
  ChevronDown,
  ChevronRight,
  FilePlus2,
  FileText,
  Folder as FolderIcon,
  FolderOpen,
  Home,
  LogOut,
  MoreHorizontal,
  Pencil,
  Plus,
  Quote,
  Search,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { APP_EVENTS, APP_NAME } from "@/lib/brand";
import { SyncStatus } from "@/components/desktop/SyncStatus";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateFolderDialog } from "./CreateFolderDialog";
import { useSettings } from "@/contexts/SettingsContext";
import {
  buildFolderTree,
  useDeleteFolder,
  useDocuments,
  useFolders,
  useInvalidateLibrary,
  type FolderNode,
} from "@/hooks/use-library";

/** Documents shown inside an expanded folder before it defers to the library. */
const FOLDER_PREVIEW_LIMIT = 5;

interface SidebarProps {
  /** Laid out beside the content rather than over it — see AppLayout. */
  docked: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const row =
  "flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-sm text-sidebar-foreground/85 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";

const sectionLabel =
  "px-2.5 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground";

/**
 * The library rail: everything to do with *getting somewhere* or *filing
 * something* lives here, and only here.
 *
 * It owns the one folder tree in the app — the dashboard used to render a
 * second, independent copy with its own expansion state and its own duplicated
 * create/rename/delete logic, so the two disagreed about which folders were
 * open. It deliberately does not list recent documents: the dashboard says
 * that better, with word counts and timestamps.
 */
export function Sidebar({ docked, isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const { openSettings } = useSettings();
  const [location, navigate] = useLocation();
  const search = useSearch();

  const { data: folders = [] } = useFolders();
  const { data: documents = [] } = useDocuments();
  const deleteFolder = useDeleteFolder();
  const invalidateLibrary = useInvalidateLibrary();

  const [expanded, setExpanded] = useState<number[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("sidebar-expanded-folders") || "[]");
    } catch {
      return [];
    }
  });
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderParent, setFolderParent] = useState<number | null>(null);
  const [renameFolder, setRenameFolder] = useState<Folder | null>(null);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded-folders", JSON.stringify(expanded));
  }, [expanded]);

  // Escape belongs to the drawer only. A docked rail is page furniture, and
  // closing it on Escape would fight whatever the user actually meant to close.
  useEffect(() => {
    if (docked || !isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [docked, isOpen, onClose]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  /**
   * Documents by their *direct* folder, newest first.
   *
   * The count badge beside a folder is a branch total — it counts everything
   * nested underneath — but the rows revealed on expand are only what lives in
   * that folder itself, because the child folders are already listed as their
   * own rows. Grouping happens here rather than in a request per folder: the
   * unfiled count already pulls the whole collection, so this is free.
   */
  const documentsByFolder = useMemo(() => {
    const grouped = new Map<number, typeof documents>();
    for (const document of documents) {
      if (document.folder_id == null) continue;
      const bucket = grouped.get(document.folder_id);
      if (bucket) bucket.push(document);
      else grouped.set(document.folder_id, [document]);
    }
    for (const bucket of grouped.values()) {
      bucket.sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
    }
    return grouped;
  }, [documents]);

  const unfiledCount = useMemo(
    () => documents.filter((document) => document.folder_id == null).length,
    [documents],
  );

  // Read from wouter, not window.location — this used to sample the URL during
  // render without subscribing, so highlights went stale on every navigation.
  const activeFolder = new URLSearchParams(search).get("folder");
  const openDocumentId = /^\/documents\/(\d+)\/edit$/.exec(location)?.[1];

  const go = (href: string) => {
    navigate(href);
    onClose();
  };

  const isActive = (href: string, folder?: string | null) =>
    location === href && (folder === undefined || activeFolder === folder);

  const activeClass = (active: boolean) =>
    `${row} ${active ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}`;

  const openFolderDialog = (parent: number | null, rename: Folder | null = null) => {
    setFolderParent(parent);
    setRenameFolder(rename);
    setFolderDialogOpen(true);
  };

  const removeFolder = (folder: Folder) => {
    // Not reversible — the folder is gone and its documents are unfiled — so
    // this one keeps a confirmation, unlike archiving a document.
    if (
      !window.confirm(
        `Delete folder “${folder.name}”? Its documents will move to No folder.`,
      )
    ) {
      return;
    }
    deleteFolder.mutate(folder);
  };

  const renderFolders = (nodes: FolderNode[], depth = 0): React.ReactNode =>
    nodes.map((folder) => {
      const isExpanded = expanded.includes(folder.id);
      const folderDocuments = documentsByFolder.get(folder.id) || [];
      // A folder with only documents in it is still worth opening, so the
      // twisty is offered for either kind of content, not just subfolders.
      const canExpand = folder.children.length > 0 || folderDocuments.length > 0;
      const preview = folderDocuments.slice(0, FOLDER_PREVIEW_LIMIT);
      return (
        <div key={folder.id}>
          <div
            className={`group flex h-9 items-center rounded-lg pr-1 hover:bg-sidebar-accent ${
              activeFolder === String(folder.id) ? "bg-sidebar-accent font-medium" : ""
            }`}
            style={{ paddingLeft: `${8 + depth * 12}px` }}
          >
            <button
              type="button"
              className="flex h-7 w-5 shrink-0 items-center justify-center text-muted-foreground"
              onClick={() =>
                canExpand &&
                setExpanded((values) =>
                  isExpanded
                    ? values.filter((id) => id !== folder.id)
                    : [...values, folder.id],
                )
              }
              aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
              aria-expanded={canExpand ? isExpanded : undefined}
            >
              {canExpand &&
                (isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                ))}
            </button>
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-sidebar-foreground/85"
              onClick={() => {
                // Opening a folder should show what is in it, both here and in
                // the main pane. Expanding only on the way *in* keeps the
                // click predictable — collapsing stays with the chevron, so a
                // second click never undoes the navigation you just made.
                if (canExpand && !isExpanded) {
                  setExpanded((values) => [...values, folder.id]);
                }
                go(`/library?folder=${folder.id}`);
              }}
            >
              {isExpanded ? (
                <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{folder.name}</span>
              {typeof folder.document_count === "number" && folder.document_count > 0 && (
                <span className="ml-auto pl-2 text-xs tabular-nums text-muted-foreground">
                  {folder.document_count}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 opacity-0 group-hover:opacity-100 focus:opacity-100"
                  aria-label={`Actions for ${folder.name}`}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => openFolderDialog(folder.id)}>
                  <Plus className="h-4 w-4" /> New subfolder
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openFolderDialog(null, folder)}>
                  <Pencil className="h-4 w-4" /> Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => removeFolder(folder)}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {isExpanded && (
            <>
              {renderFolders(folder.children, depth + 1)}
              {preview.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  className={`flex h-8 w-full items-center gap-2 rounded-lg pr-2 text-left text-[13px] transition-colors hover:bg-sidebar-accent ${
                    openDocumentId === String(document.id)
                      ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/70"
                  }`}
                  style={{ paddingLeft: `${33 + depth * 12}px` }}
                  onClick={() => go(`/documents/${document.id}/edit`)}
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{document.title || "Untitled"}</span>
                </button>
              ))}
              {folderDocuments.length > preview.length && (
                <button
                  type="button"
                  className="flex h-8 w-full items-center rounded-lg pr-2 text-left text-[13px] text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                  style={{ paddingLeft: `${33 + depth * 12}px` }}
                  onClick={() => go(`/library?folder=${folder.id}`)}
                >
                  See all {folderDocuments.length} →
                </button>
              )}
            </>
          )}
        </div>
      );
    });

  const initial = user?.username?.charAt(0).toUpperCase() || "?";

  return (
    <>
      {!docked && (
        <button
          type="button"
          aria-label="Close library"
          tabIndex={isOpen ? 0 : -1}
          onClick={onClose}
          className={`fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px] transition-opacity ${
            isOpen ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        />
      )}

      <aside
        role={docked ? undefined : "dialog"}
        aria-modal={docked ? undefined : true}
        aria-label="Library"
        aria-hidden={docked ? undefined : !isOpen}
        // A closed drawer is translated off-screen but its buttons stayed in
        // the tab order, so tabbing from the page walked into hidden
        // navigation. `inert` removes them from focus and the a11y tree
        // together, which `aria-hidden` alone cannot do.
        inert={!docked && !isOpen}
        className={
          docked
            ? "sidebar sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar"
            : `sidebar fixed inset-y-0 left-0 z-50 flex h-full w-72 flex-col border-r border-sidebar-border bg-sidebar shadow-2xl transition-transform duration-200 ease-out ${
                isOpen ? "translate-x-0" : "-translate-x-full"
              }`
        }
      >
        <div className="desktop-traffic-drag" aria-hidden="true" />
        <div className="flex h-14 shrink-0 items-center justify-between px-4">
          <div className="flex items-center gap-2 text-sidebar-foreground">
            <FileText className="h-5 w-5 text-primary" />
            <span className="font-semibold">{APP_NAME}</span>
          </div>
          {!docked && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={onClose}
              aria-label="Close library"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="space-y-2 px-3 pb-3">
          <Button
            className="h-10 w-full justify-start"
            onClick={() => go("/documents/create")}
          >
            <FilePlus2 className="mr-2 h-4 w-4" /> New document
          </Button>
          <button
            type="button"
            className={`${row} border border-sidebar-border bg-sidebar-accent/35 text-muted-foreground`}
            onClick={() => {
              onClose();
              window.dispatchEvent(new CustomEvent(APP_EVENTS.openSearch));
            }}
          >
            <Search className="h-4 w-4" />
            <span>Search</span>
            <kbd className="ml-auto text-[10px] opacity-70">⌘K</kbd>
          </button>
        </div>

        <ScrollArea className="min-h-0 flex-1 px-3">
          <nav className="pb-4" aria-label="Library sections">
            <button
              type="button"
              className={activeClass(location === "/")}
              onClick={() => go("/")}
            >
              <Home className="h-4 w-4" /> Home
            </button>
            <button
              type="button"
              className={activeClass(isActive("/library", null))}
              onClick={() => go("/library")}
            >
              <FileText className="h-4 w-4" /> Documents
            </button>
            <button
              type="button"
              className={activeClass(location === "/notes")}
              onClick={() => go("/notes")}
            >
              <BookText className="h-4 w-4" /> Notes
            </button>
            <button
              type="button"
              className={activeClass(location === "/quotes")}
              onClick={() => go("/quotes")}
            >
              <Quote className="h-4 w-4" /> Quotes
            </button>
            <button
              type="button"
              className={activeClass(location === "/scripture-references")}
              onClick={() => go("/scripture-references")}
            >
              <BookOpen className="h-4 w-4" /> Scripture
            </button>
          </nav>

          <section className="pb-4">
            <div className="flex items-center justify-between pr-1">
              <p className={sectionLabel}>Folders</p>
              <button
                type="button"
                className="mb-1.5 rounded p-0.5 text-muted-foreground hover:text-sidebar-foreground"
                onClick={() => openFolderDialog(null)}
                aria-label="New folder"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {renderFolders(tree)}
            {folders.length === 0 && (
              <p className="px-2.5 py-2 text-xs text-muted-foreground">No folders yet.</p>
            )}
            {unfiledCount > 0 && (
              <button
                type="button"
                className={`${activeClass(activeFolder === "unfiled")} mt-1 text-muted-foreground`}
                onClick={() => go("/library?folder=unfiled")}
              >
                <FileText className="h-4 w-4" />
                <span>No folder</span>
                <span className="ml-auto text-xs tabular-nums">{unfiledCount}</span>
              </button>
            )}
          </section>
        </ScrollArea>

        <div className="shrink-0 border-t border-sidebar-border p-3">
          <SyncStatus />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={`${row} h-11`}>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                  {initial}
                </span>
                <span className="min-w-0 truncate font-medium">
                  {user?.username || "Account"}
                </span>
                <MoreHorizontal className="ml-auto h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-60">
              <DropdownMenuItem onClick={() => openSettings()}>
                <Settings className="h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate("/auth");
                }}
              >
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <CreateFolderDialog
        isOpen={folderDialogOpen}
        onClose={() => {
          setFolderDialogOpen(false);
          setRenameFolder(null);
        }}
        onFolderCreated={invalidateLibrary}
        folders={folders}
        parentId={folderParent}
        renameFolder={renameFolder}
      />
    </>
  );
}
